import {
  type InlineChartSeries,
  type InlineChartSpec,
  InlineChartSpecSchema,
} from '@innermate/shared';
import { memo, useMemo } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface Props {
  raw: string;
}

type ParseResult =
  | { kind: 'ok'; spec: InlineChartSpec }
  | { kind: 'parse-error' }
  | { kind: 'schema-error'; message: string };

const SERIES_PALETTE = [
  'hsl(14 85% 55%)',
  'hsl(200 70% 48%)',
  'hsl(150 55% 42%)',
  'hsl(280 55% 58%)',
];

const AXIS_COLOR = 'hsl(var(--foreground-muted))';
const GRID_COLOR = 'hsl(var(--border-muted))';

function parseSpec(raw: string): ParseResult {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return { kind: 'parse-error' };
  }
  const result = InlineChartSpecSchema.safeParse(json);
  if (!result.success) {
    const first = result.error.issues[0];
    const path = first?.path.join('.') ?? '';
    const msg = first?.message ?? 'invalid';
    return { kind: 'schema-error', message: path ? `${path}: ${msg}` : msg };
  }
  return { kind: 'ok', spec: result.data };
}

type ChartRow = { x: string } & Record<string, number | null | string>;

function specToRows(spec: InlineChartSpec): ChartRow[] {
  return spec.x.map((label, idx) => {
    const row: ChartRow = { x: label };
    for (const s of spec.series) {
      const v = s.data[idx];
      row[s.label] = v === undefined ? null : v;
    }
    return row;
  });
}

function FallbackBlock({ raw, error }: { raw: string; error?: string }) {
  return (
    <div className="my-3">
      <pre className="overflow-x-auto rounded-md bg-code-bg p-3 font-mono text-[13px] leading-6">
        <code>{raw}</code>
      </pre>
      {error ? (
        <div className="mt-1 text-xs text-destructive">
          차트 데이터를 해석할 수 없습니다 — {error}
        </div>
      ) : null}
    </div>
  );
}

function ChartBody({ spec }: { spec: InlineChartSpec }) {
  const rows = useMemo(() => specToRows(spec), [spec]);
  const seriesList: InlineChartSeries[] = spec.series;

  const common = (
    <>
      <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" />
      <XAxis
        dataKey="x"
        stroke={AXIS_COLOR}
        tick={{ fill: AXIS_COLOR, fontSize: 12 }}
        tickLine={false}
      />
      <YAxis
        stroke={AXIS_COLOR}
        tick={{ fill: AXIS_COLOR, fontSize: 12 }}
        tickLine={false}
        label={
          spec.yLabel
            ? { value: spec.yLabel, angle: -90, position: 'insideLeft', fill: AXIS_COLOR }
            : undefined
        }
      />
      <Tooltip
        contentStyle={{
          background: 'hsl(var(--background))',
          border: '1px solid hsl(var(--border))',
          borderRadius: 6,
          fontSize: 12,
        }}
        labelStyle={{ color: 'hsl(var(--foreground))' }}
        cursor={{ stroke: GRID_COLOR }}
      />
      {seriesList.length > 1 ? <Legend wrapperStyle={{ fontSize: 12 }} /> : null}
    </>
  );

  if (spec.type === 'line') {
    return (
      <LineChart data={rows} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
        {common}
        {seriesList.map((s, i) => (
          <Line
            key={s.label}
            type="monotone"
            dataKey={s.label}
            stroke={SERIES_PALETTE[i % SERIES_PALETTE.length]}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
            connectNulls
          />
        ))}
      </LineChart>
    );
  }
  if (spec.type === 'bar') {
    return (
      <BarChart data={rows} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
        {common}
        {seriesList.map((s, i) => (
          <Bar
            key={s.label}
            dataKey={s.label}
            fill={SERIES_PALETTE[i % SERIES_PALETTE.length]}
            stackId={spec.stacked ? 'a' : undefined}
            isAnimationActive={false}
          />
        ))}
      </BarChart>
    );
  }
  return (
    <AreaChart data={rows} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
      {common}
      {seriesList.map((s, i) => {
        const color = SERIES_PALETTE[i % SERIES_PALETTE.length];
        return (
          <Area
            key={s.label}
            type="monotone"
            dataKey={s.label}
            stroke={color}
            fill={color}
            fillOpacity={0.2}
            strokeWidth={2}
            stackId={spec.stacked ? 'a' : undefined}
            isAnimationActive={false}
            connectNulls
          />
        );
      })}
    </AreaChart>
  );
}

function InlineChartImpl({ raw }: Props) {
  const parsed = useMemo(() => parseSpec(raw), [raw]);

  if (parsed.kind === 'parse-error') {
    return <FallbackBlock raw={raw} />;
  }
  if (parsed.kind === 'schema-error') {
    return <FallbackBlock raw={raw} error={parsed.message} />;
  }

  const spec = parsed.spec;
  return (
    <div className="my-3">
      {spec.title ? (
        <div className="mb-1 text-sm font-medium text-foreground-muted">{spec.title}</div>
      ) : null}
      <div style={{ width: '100%', height: 260 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ChartBody spec={spec} />
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export const InlineChart = memo(InlineChartImpl, (a, b) => a.raw === b.raw);
